import { useCallback, useEffect, useMemo, useState } from "react";
import type { PaginationState } from "@tanstack/react-table";
import client from "libs/client";
import { discoverGamesBackendAdapter } from "../../../../datagrid-configs/discoverGames/backend";
import type { SortConfig } from "../../../../datagrid-configs/types";
import type { GameRow } from "../../../../datagrid-configs/discoverGames";
import { DISCOVER_GAMES_PAGE_SIZE_OPTIONS } from "../constants";

export interface UseDiscoverGamesQueryArgs {
	storageKey: string;
	apiFilters: ReturnType<typeof discoverGamesBackendAdapter.buildFilters>;
	apiColumns: string[];
}

function readStoredPageSize(storageKey: string, defaultPageSize: number): number {
	const stored = localStorage.getItem(`${storageKey}_pageSize`);
	if (stored === null) {
		return defaultPageSize;
	}

	const parsed = Number(stored);
	if (Number.isFinite(parsed) && parsed > 0) {
		return parsed;
	}

	return defaultPageSize;
}

export function useDiscoverGamesQuery(args: UseDiscoverGamesQueryArgs) {
	const { storageKey, apiFilters, apiColumns } = args;
	const defaultPageSize = DISCOVER_GAMES_PAGE_SIZE_OPTIONS[1];

	const [pagination, setPagination] = useState<PaginationState>(() => ({
		pageIndex: 0,
		pageSize: readStoredPageSize(storageKey, defaultPageSize),
	}));

	useEffect(() => {
		localStorage.setItem(`${storageKey}_pageSize`, String(pagination.pageSize));
	}, [pagination.pageSize, storageKey]);

	const [sort, setSort] = useState<SortConfig | undefined>(undefined);

	const handleSort = useCallback((newSort?: SortConfig) => {
		setSort(newSort);
		setPagination((prev: PaginationState) => ({ ...prev, pageIndex: 0 }));
	}, []);

	const apiSort = useMemo(
		() => discoverGamesBackendAdapter.buildSort?.(sort),
		[sort],
	);

	const apiColumnsKey = JSON.stringify(apiColumns);
	const apiFiltersKey = JSON.stringify(apiFilters);
	const queryParams = useMemo(
		() => ({
			page: pagination.pageIndex + 1,
			limit: pagination.pageSize,
			columns: apiColumns.length > 0 ? apiColumns : undefined,
			filters: apiFilters.length > 0 ? apiFilters : undefined,
			order: apiSort ? { field: apiSort.field, direction: apiSort.direction as "ASC" | "DESC" } : undefined,
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps -- apiColumnsKey/apiFiltersKey for stable comparison (CountriesTable pattern)
		[pagination.pageIndex, pagination.pageSize, apiColumnsKey, apiFiltersKey, apiSort?.field, apiSort?.direction],
	);

	const { data, isLoading } = client.getDiscoverGamesDynamic.useQuery(queryParams, {
		queryKey: ["getDiscoverGamesDynamic", queryParams],
	});

	const items = useMemo(() => {
		return (data?.data?.items || []) as GameRow[];
	}, [data]);

	const pageCount = data?.data?.pages || 0;

	const queryChangeSignature = useMemo(
		() => ({
			pageIndex: pagination.pageIndex,
			pageSize: pagination.pageSize,
			apiFiltersKey,
			apiSortField: apiSort?.field,
			apiSortDirection: apiSort?.direction,
		}),
		[
			pagination.pageIndex,
			pagination.pageSize,
			apiFiltersKey,
			apiSort?.field,
			apiSort?.direction,
		],
	);

	return {
		pagination,
		setPagination,
		sort,
		handleSort,
		queryParams,
		items,
		pageCount,
		isLoading,
		queryChangeSignature,
	};
}
